'use client';

import {
	Fragment,
	useState,
	type ElementType,
	type ComponentPropsWithoutRef,
} from 'react';
import ObjectViewer from 'components/collection/object-viewer';
import CollectionObject from 'components/collection/collection-object';
import ClientPortal from 'components/helper/client-portal';
import { lofiArtworkObjectModel } from 'helpers/collection';
import type {
	GraphQLObject,
	ObjectPackageObject,
	IObjectWrapper,
	IArtworkMinimum,
} from '@metmuseum/types';
import type { GTMLinkAnalyticsProps } from 'components/controls/links/GTMLink';

interface ObjectsWithViewerProps<T extends ElementType> {
	// Wraps the rendered collection objects as a direct parent element so that
	// components like LoadMoreWrapper can operate on them one by one. Note that
	// it must also be a client component when this is rendered by a server component.
	Container?: T;
	containerProps?: Omit<ComponentPropsWithoutRef<T>, 'children'>;
	enforceMaxObjectWidth?: boolean;
	objectPackageID?: number;
	objects:
		| ObjectPackageObject[]
		| IObjectWrapper[]
		| GraphQLObject[]
		| IArtworkMinimum[];
	analytics?: GTMLinkAnalyticsProps;
}

export const ObjectsWithViewer = <T extends ElementType = typeof Fragment>({
	analytics,
	Container,
	containerProps,
	objectPackageID,
	objects,
	enforceMaxObjectWidth,
}: ObjectsWithViewerProps<T>) => {
	const ContainerComponent = Container ?? Fragment;

	const [showObjectViewer, setShowObjectViewer] = useState(false);
	const [activeObjectIndex, setActiveObjectIndex] = useState(0);
	const formattedObjects = objects.map((object) =>
		lofiArtworkObjectModel(object),
	);

	const handleObjectClick = (e, index) => {
		e.preventDefault();
		setShowObjectViewer(true);
		setActiveObjectIndex(index);
	};

	const handleCloseClick = () => setShowObjectViewer(false);

	return (
		<>
			<ContainerComponent {...containerProps}>
				{formattedObjects?.map((object, index) => {
					return (
						<CollectionObject
							index={index}
							enforceMaxWidth={enforceMaxObjectWidth}
							handleObjectClick={handleObjectClick}
							key={`collectionObject-${object.objectID}`}
							object={object}
							analytics={analytics}
						/>
					);
				})}
			</ContainerComponent>
			{showObjectViewer && (
				<ClientPortal>
					<ObjectViewer
						packageID={objectPackageID}
						objects={formattedObjects}
						initialActiveObjectIndex={activeObjectIndex}
						handleCloseClick={handleCloseClick}
					/>
				</ClientPortal>
			)}
		</>
	);
};
